import type { ClientAuditLogEntry } from 'api/audit/types'
import { createAuditLogHeader } from 'api/audit/utils'
import { AxiosRequestConfig } from 'axios'
import { farosApi } from 'faros-utils/axios'
import { Graph } from 'faros-utils/graphs'
import {
  buildClientSchema,
  getIntrospectionQuery,
  IntrospectionField,
  IntrospectionInputTypeRef,
  IntrospectionInputValue,
  IntrospectionNamedTypeRef,
  IntrospectionOutputTypeRef,
  IntrospectionType,
} from 'graphql'
import API from 'constants/api'
import { getEnv } from 'utils/environment'
import { GraphQLResponseError } from 'utils/errors'
import { GraphqlQueryResponse, IntrospectionResponse } from './types'

// Recursevly check whether the type has a nested ofType property that its name matches any of the regexes
function checkNestedOfType(
  type:
    | IntrospectionInputTypeRef
    | IntrospectionNamedTypeRef
    | IntrospectionOutputTypeRef,
  regexList: Array<RegExp>
): boolean {
  if ('ofType' in type && type.ofType) {
    if ('name' in type.ofType && type.ofType.name) {
      const { name } = type.ofType
      return regexList.some((regex) => regex.test(name))
    }

    if ('ofType' in type.ofType && type.ofType.ofType) {
      return checkNestedOfType(type.ofType, regexList)
    }
  }

  return false
}

// Filter fields based on their name and their nested ofType property
function filterFields(
  fields: Array<IntrospectionField | IntrospectionInputValue>,
  regexList: Array<RegExp>
): Array<IntrospectionField | IntrospectionInputValue> {
  const filteredFields: Array<IntrospectionField | IntrospectionInputValue> =
    fields.filter(
      (field: IntrospectionField | IntrospectionInputValue) =>
        field.name &&
        !regexList.some((regex) => regex.test(field.name)) &&
        !checkNestedOfType(field.type, regexList)
    )

  return filteredFields
}

export function filterTypes(
  type: IntrospectionType | IntrospectionField | IntrospectionInputValue,
  regexList: Array<RegExp>
): IntrospectionType | IntrospectionField | IntrospectionInputValue {
  if (typeof type !== 'object' || type === null) {
    return type
  }
  let filteredFields: Array<IntrospectionField> = []
  let filteredInputFields: Array<IntrospectionInputValue> = []

  if ('fields' in type && type.fields) {
    filteredFields = filterFields(
      [...type.fields],
      regexList
    ) as Array<IntrospectionField>

    filteredFields = filteredFields.map(
      (field: IntrospectionField) =>
        filterTypes(field, regexList) as IntrospectionField
    )
  }

  if ('inputFields' in type && type.inputFields) {
    filteredInputFields = filterFields(
      [...type.inputFields],
      regexList
    ) as Array<IntrospectionInputValue>

    filteredInputFields = filteredInputFields.map(
      (field: IntrospectionInputValue) =>
        filterTypes(field, regexList) as IntrospectionInputValue
    )
  }

  if ('kind' in type && (type.kind === 'OBJECT' || type.kind === 'INTERFACE')) {
    return {
      ...type,
      fields: filteredFields,
    }
  }

  if ('kind' in type && type.kind === 'INPUT_OBJECT') {
    return {
      ...type,
      inputFields: filteredInputFields,
    }
  }

  return type
}

export enum FILTER_TYPE {
  NoData = 'no-data',
  None = 'none',
  Aggregate = 'aggregate',
}

export const fetchGraphQLIntrospection = (
  graph: Graph,
  config?: AxiosRequestConfig,
  filterType?: FILTER_TYPE
) =>
  farosApi
    .post<{ query: string }, IntrospectionResponse>(
      API.GRAPHS_GRAPHQL({ graphName: graph.name }),
      { query: getIntrospectionQuery() },
      { ...config, graph }
    )
    .then(async (response) => {
      if (!response || !response.data) {
        throw new Error('GraphQL introspection response does not include data')
      }

      const introspection = response.data
      let filteredTypes = introspection.__schema.types

      // if filterType is 'aggregate' or 'no-data', remove all types that contain the word aggregate
      if (
        filterType === FILTER_TYPE.Aggregate ||
        filterType === FILTER_TYPE.NoData
      ) {
        filteredTypes = filteredTypes.map((mtype) =>
          filterTypes(mtype, [/aggregate/])
        ) as Array<IntrospectionType>
      }

      // if filterType is 'no-data', remove all types that have no data
      if (filterType === FILTER_TYPE.NoData) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const emptyTypesNames = await getEmptyTypes(graph, [...filteredTypes])
        const regexList: Array<RegExp> = emptyTypesNames.map(
          (name) => new RegExp(name)
        )

        filteredTypes = filteredTypes.map((mtype) =>
          filterTypes(mtype, regexList)
        ) as Array<IntrospectionType>
      }

      const filteredIntrospection = {
        ...introspection,
        __schema: {
          ...introspection.__schema,
          types: filteredTypes,
        },
      }

      return filteredIntrospection
    })
    .then((res) => {
      return {
        introspection: { data: res } as IntrospectionResponse,
        schema: buildClientSchema(res),
      }
    })

type GraphQLVariables = {
  [key: string]:
    | null
    | string
    | number
    | boolean
    | GraphQLVariables
    | Array<GraphQLVariables>
}

export const fetchGraphQLQuery = <TResponseData, TVariables = GraphQLVariables>(
  graph: Graph,
  query: string,
  variables?: TVariables,
  config?: AxiosRequestConfig & { auditLogEntry?: ClientAuditLogEntry }
) => {
  const queryVariables =
    getEnv('NEXT_PUBLIC_FAROS_GRAPHQL_NO_STALE_READS') === 'true'
      ? { noStaleReads: 'true', ...variables }
      : variables
  return farosApi
    .post<unknown, GraphqlQueryResponse<TResponseData>>(
      API.GRAPHS_GRAPHQL({ graphName: graph.name }),
      { query, variables: queryVariables },
      {
        headers: config?.auditLogEntry
          ? createAuditLogHeader(config.auditLogEntry)
          : undefined,
        ...config,
        graph,
      }
    )
    .then((res) => {
      if (res.errors) {
        throw new GraphQLResponseError(res.errors)
      }
      return res?.data
    })
}

// Helper function to split an array into chunks
function chunkArray<T>(array: Array<T>, chunkSize: number): Array<Array<T>> {
  const chunks: Array<Array<T>> = []
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

const getEmptyTypes = async (graph: Graph, types: Array<IntrospectionType>) => {
  let returnTypes: Array<string> = []

  const queryRootType = types.find((type) => type.name === 'query_root')
  if (
    queryRootType &&
    'fields' in queryRootType &&
    queryRootType.fields &&
    queryRootType.fields
  ) {
    const listOfTypes = queryRootType.fields.map((field) => field.name)
    const typeChunks = chunkArray(listOfTypes, 500) // chunk the list of types into chunks of 500

    // for each chunk, create a query that fetches the first element of each type and execute it
    const results: Array<Record<string, Array<string>>> = await Promise.all(
      typeChunks.map(async (chunk) => {
        let query = 'query {'

        chunk.forEach((type) => {
          query += `
              ${type}(limit: 1){
               id
              }
            `
        })

        query += '}'

        return fetchGraphQLQuery(graph, query)
      })
    )

    // for each result, remove the types that have data
    results.forEach((originalResult) => {
      const result = { ...originalResult }

      Object.keys(result).forEach((type) => {
        if (Array.isArray(result[type]) && result[type].length > 0) {
          delete result[type]
        }
      })

      returnTypes = returnTypes.concat(Object.keys(result))
    })
  }

  return returnTypes
}
